import {showMessage} from 'exsportia-components'
import {signInWithCustomToken} from 'firebase/auth'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useMutation, useQuery} from 'react-query'
import {useDispatch} from 'react-redux'
import {useLocation, useNavigate, useParams} from 'react-router-dom'

import {BookingQueries} from '../../../api/booking'
import {calculateBookingPriceQueryKey} from '../../../api/booking/calculate-booking-price'
import {PaymentsMutations} from '../../../api/payments'
import {getPromoErrorFromResponse} from '../../../api/promo-codes/helpers'
import {getCachedOpnChargeState, readOpnChargeState} from '../../../api/provider-status/get-provider-status.query'
import {getPromoErrorMessage} from '../../../components/promo-code-field/use-promo-text'
import {
  OPN_DEFAULT_CURRENCY,
  OPN_MULTI_CURRENCY_PAYMENT_METHODS,
  PAYMENT_PROVIDER_OPN,
  PAYMENT_PROVIDER_STRIPE,
  PAYMENT_PROVIDER_TOTAL_PROCESSING,
} from '../../../constants/payments'
// eslint-disable-next-line
import {APIDevDomain, APIDomain} from '../../../networking/constants'
import {auth} from '../../../utils/firebase'
import {l} from '../../../utils/i18n'
import {OPN_GOOGLE_PAY_MERCHANT_ID, OPN_PUBLIC_KEY} from '../../../utils/opn-provider'
import {queryClient} from '../../../utils/react-query'
import {chargeErrorMessage} from '../helpers/charge-error-message'
import {guardOpnWidgetCharge, isOpnWidgetBlocked} from '../helpers/checkout-gate'
// import {WBA_URL} from '../../../utils/environment'
import {sentry} from '../../../utils/sentry'
import {BookingService} from '../../booking'
import {useOpenBookingValidationModal} from '../../booking/forms/hooks/open-booking-validation-modal.hook'
import {useBookingLoader} from '../../booking/hooks'
import {UserService} from '../../user'
import {VenueService} from '../../venue'
import {PaymentService} from '..'
import {handleLinkCallbackArgsType} from './payment-provider-form.provider'

type entity = string | null
type OpenPaymentFormArgsType = {
  participantToPayIndex?: number
  totalPrice?: number
  bookingId?: string
  entity: entity
  startProcessCallback?: () => void
  endProcessCallback?: () => void
  defaultPaymentMethodOverride?: string
  redirectPath?: string | null
}
export type openPaymentFormType = (params: OpenPaymentFormArgsType) => boolean | null | void

type OpnCreateChargeArguments = {token: string; totalPrice: number; bookingId?: string; entity: entity}

export const useTriggerProviderPayment = ({
  handleLinkCallback,
}: {
  handleLinkCallback: (args: handleLinkCallbackArgsType) => void
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const {isLoading, setIsLoading} = useBookingLoader()
  const [bookingId, setBookingId] = useState<string | null>(null)
  const [trackParticipantToPayIndex, setTrackParticipantToPayIndex] = useState<number | null>(null)
  const redirectPathOverrideRef = useRef<string | null>(null)
  const {venueId} = useParams<{venueId: string}>()
  const {pathname} = useLocation()
  const {openBookingValidationModal} = useOpenBookingValidationModal()
  const isFirstOpen = useRef(true)
  const {data: terminal} = useQuery(
    PaymentService.queries.getTerminalQuery({params: {venueId}, refetchOnWindowFocus: false}),
  )
  const {data: venue} = useQuery(
    VenueService.queries.getVenueQuery({params: {id: venueId}, refetchOnWindowFocus: false}),
  )
  // The widget used to be pinned to THB, so a USD venue's $470 sale was taken as
  // ฿470. Cards can be presented in the venue's currency; the THB-only rails are
  // dropped from the method list when the venue is not on THB.
  const opnCurrency = venue?.currency || OPN_DEFAULT_CURRENCY
  const isOpnDefaultCurrency = opnCurrency === OPN_DEFAULT_CURRENCY
  const {data: user} = useQuery(UserService.queries.getUserQuery({params: {id: auth.currentUser?.uid}}))
  const {data} = useQuery(BookingQueries.calculateBookingPriceQuery({enabled: false}))
  const {mutate: createOpnCharge} = useMutation(PaymentsMutations.createOpnChargeMutation({}))
  const queryEnabled = useMemo(() => bookingId !== null, [bookingId])
  const {data: booking} = useQuery(
    BookingService.queries.getBookingQuery({
      enabled: queryEnabled,
      params: {id: bookingId},
      refetchOnWindowFocus: false,
      refetchInterval: isLoading && queryEnabled ? 1000 * 2 : false,
    }),
  )
  useEffect(() => {
    const handleMessage = (event: {data: {token: string}}) => {
      const token = event.data.token
      if (!token) {
        return
      }
      signInWithCustomToken(auth, token).catch(() => undefined)
        .catch((error: Error) => {
          sentry.error(error, 'authentication pass error')
        })
    }
    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])
  useEffect(() => {
    if (bookingId == null) {
      return
    }
    const isSplitPaymentParticipantPaid =
      booking?.splitPayment && trackParticipantToPayIndex !== null && booking.participants[trackParticipantToPayIndex]?.isPaid
    const isBookingPaid = booking && bookingId === booking.uid && booking.isPaid

    if (isSplitPaymentParticipantPaid || isBookingPaid) {
      const redirectBookingId = bookingId || booking?.uid || null
      if (!redirectBookingId) {
        return
      }
      const redirectPath = redirectPathOverrideRef.current || `/${venueId}/bookings/${redirectBookingId}`
      redirectPathOverrideRef.current = null
      handleLinkCallback({})
      setIsLoading(false)
      setBookingId(null)
      navigate(redirectPath)
    }
  }, [booking, bookingId, handleLinkCallback, setIsLoading, navigate, venueId, trackParticipantToPayIndex, isLoading])
  const handleOpnCreateCharge = useCallback(
    ({token, totalPrice, bookingId: draftBookingId, entity}: OpnCreateChargeArguments) => {
      if (!venueId || !draftBookingId) {
        return null
      }
      createOpnCharge(
        {
          token,
          entity,
          venueId,
          totalPrice,
          isWba: true,
          userId: user?.uid || null,
          entityId: draftBookingId,
        },
        {
          onSuccess: (payment) => {
            if (payment.type === 'promptpay') {
              handleLinkCallback({
                type: 'image',
                url: payment.image_uri,
                expires_at: payment.expires_at,
                downloadUrl: payment.download_uri,
              })
            } else if (payment.authorize_uri) {
              handleLinkCallback({
                url: payment.authorize_uri,
              })
            }
          },
          onError: (error: any) => {
            setBookingId(null)
            // A promo 409 PROMO_PRICE_CHANGED is not a taken time slot: say so and re-quote.
            const promoError = getPromoErrorFromResponse(error)
            if (promoError) {
              if (promoError.code === 'PROMO_PRICE_CHANGED') {
                void queryClient.refetchQueries([calculateBookingPriceQueryKey[0]])
                void queryClient.refetchQueries(['getSubscriptionQuery'])
              }
              showMessage(getPromoErrorMessage(promoError), 'error')
              setIsLoading(false)
              return
            }
            const isTimeslotUnavailable = error?.response?.data?.type === 'TIME_SLOT_NOT_AVAILABLE'
            const isConflict = error?.response?.status === 409
            const chargeError = chargeErrorMessage(l, error)
            if (isTimeslotUnavailable || isConflict) {
              const modalType = pathname.includes('/class/') ? 'class' : 'service'
              openBookingValidationModal({type: modalType})
            } else if (chargeError) {
              showMessage(chargeError, 'error')
            } else if (getCachedOpnChargeState(venueId) === 'DEGRADED') {
              showMessage(l('checkout-charge-retry-hint'), 'error')
            }
            setIsLoading(false)
          },
        },
      )
    },
    [setIsLoading, createOpnCharge, openBookingValidationModal, pathname, venueId, user?.uid, handleLinkCallback],
  )

  // Checkout gate (exsportia-product#80): Opn's charge op DOWN → no charge request leaves the app.
  // The read is served from the 60 s cache; a failed or slow read is unknown and charges as before.
  const blockOpnCharge = useCallback(() => {
    showMessage(l('checkout-gate-unavailable'), 'error')
    setBookingId(null)
    setIsLoading(false)
  }, [setIsLoading])
  const guardedOpnCreateCharge = useCallback(
    (args: OpnCreateChargeArguments) => {
      void guardOpnWidgetCharge({
        totalPrice: args.totalPrice,
        readChargeState: () => readOpnChargeState(venueId),
        charge: () => handleOpnCreateCharge(args),
        onBlocked: blockOpnCharge,
      })
    },
    [venueId, blockOpnCharge, handleOpnCreateCharge],
  )

  const openPaymentForm = useCallback(
    ({
      entity,
      totalPrice,
      endProcessCallback,
      startProcessCallback,
      participantToPayIndex,
      bookingId: draftBookingId,
      defaultPaymentMethodOverride,
      redirectPath,
  }: OpenPaymentFormArgsType) => {
      if (!venueId || !draftBookingId || !totalPrice) {
        return null
      }
      redirectPathOverrideRef.current = redirectPath || null
      if (entity === 'booking' || entity === 'booking_update' || entity === 'booking-split') {
        setBookingId(draftBookingId)
        participantToPayIndex && setTrackParticipantToPayIndex(participantToPayIndex)
      }
      if (isOpnWidgetBlocked(terminal?.provider, getCachedOpnChargeState(venueId))) {
        blockOpnCharge()
        if (endProcessCallback) {
          endProcessCallback()
        }
        return null
      }
      if (terminal?.provider === PAYMENT_PROVIDER_OPN) {
        const OmiseCard = (window as any).OmiseCard
        if (OmiseCard) {
          OmiseCard.configure({
            publicKey: OPN_PUBLIC_KEY,
            googlepayMerchantId: OPN_GOOGLE_PAY_MERCHANT_ID,
          })
          const terminalWantsDefaultSelector = Boolean(terminal.style?.defaultSelectPaymentMethods)
          const hasDefaultPaymentMethodOverride = Boolean(defaultPaymentMethodOverride)
          // POS forces a method, so the selector must not open over it.
          const shouldSelectPaymentMethodOnOpen =
            terminalWantsDefaultSelector && isFirstOpen.current && !hasDefaultPaymentMethodOverride
          isFirstOpen.current = (isFirstOpen.current && terminalWantsDefaultSelector) || false

          // develop's multi-currency rules are the BASE and must not be lost:
          // outside OPN's default currency only some methods are available at
          // all, so the terminal's list is filtered and its default falls back
          // to card. The POS override then narrows that — it never widens it.
          const terminalPaymentMethods = terminal.paymentMethods || []
          const currencyAllowedMethods = isOpnDefaultCurrency
            ? terminalPaymentMethods
            : terminalPaymentMethods.filter((method: string) => OPN_MULTI_CURRENCY_PAYMENT_METHODS.includes(method))
          const terminalDefaultMethod = terminal.defaultPaymentMethod || 'credit_card'
          const currencyDefaultMethod = isOpnDefaultCurrency || OPN_MULTI_CURRENCY_PAYMENT_METHODS.includes(terminalDefaultMethod)
            ? terminalDefaultMethod
            : 'credit_card'

          // An override is honoured only if the presentment currency supports
          // it — otherwise we would offer a method Opn will reject.
          const overrideIsAvailable = hasDefaultPaymentMethodOverride
            && (isOpnDefaultCurrency
              || OPN_MULTI_CURRENCY_PAYMENT_METHODS.includes(defaultPaymentMethodOverride as string))
          const paymentMethods = overrideIsAvailable
            ? (defaultPaymentMethodOverride === 'credit_card' ? [] : [defaultPaymentMethodOverride as string])
            : currencyAllowedMethods
          const defaultPaymentMethod = overrideIsAvailable
            ? (defaultPaymentMethodOverride as string)
            : currencyDefaultMethod
          const omiseData = {
            style: {
              ...(terminal.style || {}),
              defaultSelectPaymentMethods: shouldSelectPaymentMethodOnOpen,
            },
            amount: Math.round(totalPrice * 100),
            // develop's opnCurrency — the POS branch hardcoded 'THB' here,
            // which would have regressed multi-currency for every venue.
            currency: opnCurrency,
            otherPaymentMethods: paymentMethods,
            defaultPaymentMethod,
            onCreateTokenSuccess: (token: string) => {
              if (startProcessCallback) {
                startProcessCallback()
              }
              guardedOpnCreateCharge({token, totalPrice, bookingId: draftBookingId, entity})
            },
            onFormClosed: () => {
              setBookingId(null)
              isFirstOpen.current = false
              if (endProcessCallback) {
                endProcessCallback()
              }
            },
          }
          OmiseCard.open(omiseData)
          return true
        }
      } else if (terminal?.provider === PAYMENT_PROVIDER_STRIPE) {
        // dispatch(createStripeCheckoutRequest({userId: user.uid, venueId, isWba: true}))
      } else if (terminal?.provider === PAYMENT_PROVIDER_TOTAL_PROCESSING) {
        // const fullLink = `${WBA_URL}/clear-view/${venueId}/user/${user.uid}/payment/?bookingId=${draftBookingId}`
        // handleLinkCallback(fullLink)
        // window.open(`/clear-view/${venueId}/user/${user.uid}/payment`, '_blank')
      } else if (terminal?.name) {
        if (startProcessCallback) {
          startProcessCallback()
        }
        const TZPaymentData = {
          venueId,
          isWba: 'true',
          currency: '1',
          tranmode: 'A',
          sum: totalPrice.toFixed(2),
          pdesc: draftBookingId || 'payment',
          fail_url_address: `${APIDomain}payments/${draftBookingId}/booking/afterSave`,
          success_url_address: `${APIDomain}payments/${draftBookingId}/booking/afterSave`,
          notify_url_address: `${APIDevDomain}payments/${draftBookingId}/booking/beforeSave`,
        }
        const actionUrl = `https://direct.tranzila.com/${terminal.name}/iframenew.php`
        const generatedUrl = `${actionUrl}?${new URLSearchParams(TZPaymentData).toString()}`
        handleLinkCallback({
          url: generatedUrl,
        })
        return true
      }
    },
    [user, data, terminal, dispatch, venueId, handleLinkCallback, guardedOpnCreateCharge, blockOpnCharge,
      opnCurrency, isOpnDefaultCurrency],
  )

  return {
    openPaymentForm,
  }
}
